// Dialoge der Teamverwaltung (design_handoff_team §5, Screens 05–07): Mitarbeiter bearbeiten / anlegen und die
// Lösch-Bestätigung. Fachlogik unverändert aus TeamMitarbeiterEditModal / TeamMitarbeiterErstellenModal:
// Avatar-Upload in avatars/public/team, Anlegen per auth.signUp über einen zweiten Client ohne Sitzung + profiles.upsert,
// Bearbeiten per profiles.update inkl. ist_angestellt / gps_beim_stempeln und zuweisbar beim (Ent-)Archivieren (#516).
import { useEffect, useRef, useState, type ChangeEvent, type FormEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { createClient } from '@supabase/supabase-js';
import {
  Buildings, Camera, CaretUpDown, CheckCircle, CircleNotch, EnvelopeSimple, FloppyDisk, HardHat, IdentificationBadge, Lock,
  MapPin, PencilSimple, ShieldCheck, Sparkle, Trash, User, UserCircle, UserPlus, Users, WarningCircle, type Icon,
} from '@phosphor-icons/react';
import { supabase, supabaseAnonKey, supabaseUrl } from '../../../../lib/supabase/client';
import { istArchiviert } from '../../../../lib/team/archiv';
import { initialen, type TeamMitglied } from '../../../../lib/team/mitarbeiter';
import { DialogHuelle } from '../../../../components/immobilien/ui';
import { useDialogFokus } from '../../../../components/immobilien/ui/fokus';
import { IosSchalter } from '../../../../components/ui/IosSchalter';
import { FeldRahmen } from './Bausteine';

const fehlerText = (err: unknown, rueckfall: string): string =>
  (err instanceof Error ? err.message : (err as { message?: string } | null)?.message) || rueckfall;

const ROLLEN: { id: string; label: string; icon: Icon; nurAnlegen?: boolean }[] = [
  { id: 'admin', label: 'Admin', icon: ShieldCheck },
  { id: 'team', label: 'Team', icon: Users },
  { id: 'eigentuemer', label: 'Eigentümer', icon: UserCircle },
  { id: 'mieter', label: 'Mieter', icon: Buildings },
  { id: 'dienstleister', label: 'Dienstleister', icon: HardHat, nurAnlegen: true },
  // Hausmeister sehen NUR freigegebene Tickets (eigene Oberfläche, kein Verwaltungsmenü)
  { id: 'hausmeister', label: 'Hausmeister', icon: HardHat },
];

const STATI: { wert: string; label: string; tip: string; punkt: string; nurBearbeiten?: boolean }[] = [
  { wert: 'Aktiv', label: 'Aktiv', tip: 'Aktiv – Zugang ist aktiv', punkt: 'var(--ok)' },
  { wert: 'Inaktiv', label: 'Inaktiv', tip: 'Inaktiv – Zugang ist ruhend', punkt: 'var(--ink3)' },
  { wert: 'Archiviert', label: 'Archiviert', tip: 'Archiviert – aus allen Auswahllisten entfernt', punkt: 'var(--warn)', nurBearbeiten: true },
];

interface Formular {
  full_name: string; email: string; password: string; avatar_url: string; role: string; status: string;
  ist_angestellt: boolean; gps_beim_stempeln: boolean;
}

const LEER: Formular = { full_name: '', email: '', password: '', avatar_url: '', role: 'team', status: 'Aktiv', ist_angestellt: true, gps_beim_stempeln: false };

function Feld({ label, icon: I, children }: { label: string; icon: Icon; children: ReactNode }) {
  return (
    <label className="flex min-w-0 flex-col" style={{ gap: 6 }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>{label}</span>
      <FeldRahmen>
        <I size={16} color="var(--ink3)" />
        {children}
      </FeldRahmen>
    </label>
  );
}

const eingabe = { flex: 1, minWidth: 0, height: '100%', border: 'none', outline: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 15, fontWeight: 700, color: 'var(--ink)' } as const;

function SchalterZeile({ icon: I, fg, bg, titel, text, tip, an, onChange }: { icon: Icon; fg: string; bg: string; titel: string; text: string; tip: string; an: boolean; onChange: (an: boolean) => void }) {
  return (
    <div data-tip={tip} onClick={() => onChange(!an)} className="flex items-start" style={{ gap: 12, padding: 14, cursor: 'pointer' }}>
      <span className="flex shrink-0 items-center justify-center" style={{ width: 34, height: 34, borderRadius: 11, background: bg }}>
        <I size={17} weight="fill" color={fg} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 3 }}>
        <span style={{ fontSize: 14, fontWeight: 800 }}>{titel}</span>
        <span style={{ fontSize: 12.5, lineHeight: 1.45, color: 'var(--ink3)' }}>{text}</span>
      </span>
      <IosSchalter an={an} onChange={onChange} label={titel} />
    </div>
  );
}

/** Bearbeiten (mitglied gesetzt) bzw. Anlegen (mitglied = null). */
export function MitarbeiterDialog({ mitglied, onSchliessen, onErfolg }: { mitglied: TeamMitglied | null; onSchliessen: () => void; onErfolg: () => void }) {
  const anlegen = mitglied === null;
  const datei = useRef<HTMLInputElement>(null);
  const [f, setF] = useState<Formular>(() => (mitglied ? {
    full_name: mitglied.full_name || '', email: mitglied.email || '', password: '', avatar_url: mitglied.avatar_url || '',
    role: mitglied.role || 'team', status: mitglied.status || 'Aktiv',
    ist_angestellt: mitglied.ist_angestellt !== false, gps_beim_stempeln: mitglied.gps_beim_stempeln === true,
  } : LEER));
  const [laeuft, setLaeuft] = useState(false);
  const [laedtHoch, setLaedtHoch] = useState(false);
  const [fertig, setFertig] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  // Nach dem Erfolg kurz bestätigen (wie bisher 1,8 s), dann neu laden und schließen.
  const rueckruf = useRef({ onErfolg, onSchliessen });
  rueckruf.current = { onErfolg, onSchliessen };
  useEffect(() => {
    if (!fertig) return;
    const t = window.setTimeout(() => { rueckruf.current.onErfolg(); rueckruf.current.onSchliessen(); }, 1800);
    return () => window.clearTimeout(t);
  }, [fertig]);

  const setze = <K extends keyof Formular>(k: K, v: Formular[K]) => setF((alt) => ({ ...alt, [k]: v }));
  const aendern = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setze(e.target.name as 'full_name' | 'email' | 'password' | 'role', e.target.value);

  const hochladen = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setFehler(null);
    setLaedtHoch(true);
    try {
      const ext = file.name.split('.').pop();
      const pfad = `public/team/${anlegen ? Math.random() : mitglied.id}_${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from('avatars').upload(pfad, file, { cacheControl: '3600', upsert: !anlegen });
      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(pfad);
      setze('avatar_url', publicUrl);
    } catch (err) {
      setFehler(fehlerText(err, 'Bild-Upload fehlgeschlagen.'));
    } finally {
      setLaedtHoch(false);
    }
  };

  const speichern = async (e: FormEvent) => {
    e.preventDefault();
    if (laeuft || laedtHoch) return;
    setLaeuft(true);
    setFehler(null);
    try {
      if (anlegen) {
        // Zweiter Client ohne Sitzung: signUp darf die Anmeldung des Admins nicht ersetzen.
        const temp = createClient(supabaseUrl, supabaseAnonKey, { auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false } });
        const { data: authData, error: authError } = await temp.auth.signUp({
          email: f.email, password: f.password,
          options: { data: { full_name: f.full_name, avatar_url: f.avatar_url, role: f.role } },
        });
        if (authError) throw authError;
        if (!authData.user) throw new Error('Authentifizierung fehlgeschlagen.');
        const { error: profileError } = await supabase.from('profiles').upsert({
          id: authData.user.id, full_name: f.full_name, email: f.email, avatar_url: f.avatar_url, role: f.role, status: f.status,
        }, { onConflict: 'id' });
        if (profileError) throw profileError;
      } else {
        const { error } = await supabase.from('profiles').update({
          full_name: f.full_name, email: f.email, avatar_url: f.avatar_url, role: f.role, status: f.status,
          // #516: Archivierte sind nirgends mehr auswählbar; beim Zurückholen wieder zuweisbar
          ...(istArchiviert(f) ? { zuweisbar: false } : istArchiviert(mitglied) ? { zuweisbar: true } : {}),
          ist_angestellt: f.ist_angestellt, gps_beim_stempeln: f.gps_beim_stempeln,
        }).eq('id', mitglied.id);
        if (error) throw error;
      }
      setFertig(true);
    } catch (err) {
      setFehler(fehlerText(err, anlegen ? 'Fehler beim Anlegen.' : 'Fehler beim Speichern.'));
    } finally {
      setLaeuft(false);
    }
  };

  const rollen = ROLLEN.filter((r) => anlegen || !r.nurAnlegen);
  const RollenIcon = rollen.find((r) => r.id === f.role)?.icon ?? Users;
  const stati = STATI.filter((s) => !anlegen || !s.nurBearbeiten);

  return (
    <DialogHuelle titel={anlegen ? 'Mitarbeiter' : 'Profil anpassen'} untertitel={anlegen ? 'Neuaufnahme' : 'Bearbeitungs-Modus'}
      icon={anlegen ? UserPlus : PencilSimple} iconFarbe={{ farbe: 'var(--acc-ink)', flaeche: 'var(--acc-fill)' }}
      onSchliessen={onSchliessen} laeuft={laeuft || laedtHoch || fertig} breite={600}>
      {fertig ? (
        <div className="flex flex-col items-center text-center" style={{ padding: '30px 10px', gap: 10 }}>
          <span className="flex items-center justify-center" style={{ width: 72, height: 72, borderRadius: '50%', background: 'var(--ok-soft)' }}>
            <CheckCircle size={42} weight="fill" color="var(--ok)" />
          </span>
          <span role="status" style={{ fontSize: 20, fontWeight: 800 }}>{anlegen ? 'Erstellt' : 'Erfolgreich'}</span>
          <span style={{ fontSize: 14, color: 'var(--ink2)' }}>{anlegen ? 'Der Mitarbeiter-Zugang wurde erfolgreich angelegt.' : 'Profil wurde aktualisiert.'}</span>
        </div>
      ) : (
        <form onSubmit={(e) => void speichern(e)} className="flex flex-col" style={{ gap: 16 }}>
          {fehler && (
            <div role="alert" className="flex items-center" style={{ borderRadius: 14, padding: '10px 12px', gap: 8, background: 'var(--bad-soft)', color: 'var(--bad)', fontSize: 13, fontWeight: 700 }}>
              <WarningCircle size={16} weight="fill" className="shrink-0" />{fehler}
            </div>
          )}

          <div className="flex items-center" style={{ gap: 16 }}>
            <input ref={datei} type="file" accept="image/*" onChange={(e) => void hochladen(e)} className="hidden" />
            <button type="button" onClick={() => datei.current?.click()} disabled={laedtHoch} data-tip="Foto – Profilbild hochladen" aria-label="Profilbild hochladen"
              className="relative flex shrink-0 items-center justify-center"
              style={{ width: 84, height: 84, padding: 0, border: 'none', borderRadius: '50%', cursor: laedtHoch ? 'wait' : 'pointer', background: 'var(--acc-fill)', color: 'var(--acc-ink)',
                boxShadow: 'var(--acc-sh)', fontFamily: 'inherit', fontSize: 28, fontWeight: 800 }}>
              {f.avatar_url
                ? <img src={f.avatar_url} alt="" className="h-full w-full object-cover" style={{ borderRadius: '50%', opacity: laedtHoch ? 0.3 : 1 }} />
                : (!laedtHoch && f.full_name.trim() ? initialen(f.full_name) : null)}
              {laedtHoch && <CircleNotch size={24} weight="bold" className="absolute animate-spin" />}
              <span className="absolute flex items-center justify-center"
                style={{ right: -2, bottom: -2, width: 30, height: 30, borderRadius: '50%', background: 'var(--sheet)', boxShadow: '0 4px 10px var(--shade), inset 0 0 0 1px var(--line)' }}>
                <Camera size={15} weight="fill" color="var(--acc)" />
              </span>
            </button>
            <div className="min-w-0 flex-1">
              <Feld label="Vollständiger Name" icon={User}>
                <input required name="full_name" value={f.full_name} onChange={aendern} placeholder={anlegen ? 'Vorname Nachname' : 'Name'} style={eingabe} />
              </Feld>
            </div>
          </div>

          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            <Feld label="E-Mail Adresse" icon={EnvelopeSimple}>
              <input required type="email" name="email" value={f.email} onChange={aendern} placeholder="name@mail.de" style={eingabe} />
            </Feld>
            {anlegen && (
              <Feld label="Passwort" icon={Lock}>
                <input required type="password" name="password" value={f.password} onChange={aendern} placeholder="••••••••" autoComplete="new-password" style={eingabe} />
              </Feld>
            )}
          </div>

          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            <label className="flex min-w-0 flex-col" style={{ gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Rolle</span>
              <FeldRahmen>
                <RollenIcon size={16} weight="fill" color="var(--acc)" />
                <select name="role" value={f.role} onChange={aendern} style={{ ...eingabe, cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none' }}>
                  {rollen.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                </select>
                <CaretUpDown size={13} weight="bold" color="var(--ink3)" />
              </FeldRahmen>
            </label>
            <div className="flex min-w-0 flex-col" style={{ gap: 6 }}>
              <span id="mitarbeiter-status" style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Status</span>
              <div role="radiogroup" aria-labelledby="mitarbeiter-status" className="flex" style={{ height: 48, padding: 3, borderRadius: 14, background: 'var(--fill2)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
                {stati.map((s) => {
                  const an = f.status === s.wert;
                  return (
                    <button key={s.wert} type="button" role="radio" aria-checked={an} data-tip={s.tip} onClick={() => setze('status', s.wert)}
                      className="flex min-w-0 flex-1 items-center justify-center"
                      style={{ gap: 6, border: 'none', borderRadius: 11, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 800,
                        background: an ? 'var(--bg)' : 'transparent', color: an ? 'var(--ink)' : 'var(--ink2)', boxShadow: an ? '0 2px 8px -3px var(--shade)' : 'none' }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: an ? s.punkt : 'var(--ink3)', flexShrink: 0 }} />
                      <span className="truncate">{s.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {!anlegen && (
            <div className="flex flex-col" style={{ borderRadius: 20, background: 'var(--fill)' }}>
              {/* Belegschaft: steuert NUR die Personallisten – die Rechte des Zugangs bleiben unverändert. */}
              <SchalterZeile icon={IdentificationBadge} fg="var(--acc)" bg="var(--acc-soft)" titel="Gehört zur Belegschaft"
                tip="Gehört zur Belegschaft – Steuert nur die Personallisten" an={f.ist_angestellt} onChange={(v) => setze('ist_angestellt', v)}
                text="Abwählen bei Entwickler- oder reinen Administrationszugängen: Die Person erscheint dann nicht in Zeiterfassung, Abwesenheiten, Teamkalender und Personalakte. Die Rechte des Zugangs ändern sich dadurch nicht." />
              <span aria-hidden style={{ height: 1, margin: '0 14px', background: 'var(--line)' }} />
              {/* Standort nur, wo er gebraucht wird — Außendienst und Hausmeister. */}
              <SchalterZeile icon={MapPin} fg="var(--info)" bg="var(--info-soft)" titel="Standort beim Einstempeln"
                tip="Standort beim Einstempeln – Nur einmalig beim Kommen" an={f.gps_beim_stempeln} onChange={(v) => setze('gps_beim_stempeln', v)}
                text="Nur für Außendienst und Hausmeister sinnvoll. Der Standort wird einmalig beim Kommen gespeichert, nicht laufend verfolgt. Lehnt das Gerät ab oder ist kein Empfang da, wird trotzdem gestempelt." />
            </div>
          )}

          <div className="flex flex-wrap items-center" style={{ gap: 10, paddingTop: 4 }}>
            <span className="flex flex-1 items-center whitespace-nowrap" style={{ gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>
              <ShieldCheck size={14} weight="fill" color="var(--ok)" />{anlegen ? 'Sicherer Admin-Modus' : 'Datenschutz-Verifiziert'}
            </span>
            <button type="button" onClick={onSchliessen} disabled={laeuft}
              style={{ height: 48, padding: '0 18px', border: 'none', borderRadius: 16, cursor: 'pointer', fontFamily: 'inherit', fontSize: 15, fontWeight: 800, background: 'var(--fill2)', color: 'var(--ink)' }}>
              Abbrechen
            </button>
            <button type="submit" disabled={laeuft || laedtHoch} className="flex items-center justify-center"
              style={{ height: 48, padding: '0 22px', gap: 8, border: 'none', borderRadius: 16, cursor: laeuft ? 'wait' : 'pointer', fontFamily: 'inherit', fontSize: 15, fontWeight: 800,
                background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)', opacity: laeuft || laedtHoch ? 0.6 : 1 }}>
              {laeuft ? <CircleNotch size={15} weight="bold" className="animate-spin" /> : anlegen ? <Sparkle size={15} weight="bold" /> : <FloppyDisk size={15} weight="bold" />}
              {anlegen ? 'Erstellen' : 'Speichern'}
            </button>
          </div>
        </form>
      )}
    </DialogHuelle>
  );
}

/** Rückfrage im Stil von Screen 07: Icon zentriert, Titel, Text, roter Knopf und „Abbrechen“ untereinander. */
export function GefahrDialog({ titel, text, knopf, tip, laeuft, onBestaetigen, onAbbrechen, icon: I = WarningCircle }: {
  titel: string; text: ReactNode; knopf: string; tip?: string; laeuft: boolean; onBestaetigen: () => void; onAbbrechen: () => void; icon?: Icon;
}) {
  const dialog = useDialogFokus<HTMLDivElement>();
  const abbrechen = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    abbrechen.current?.focus();
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape' && !laeuft) onAbbrechen(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onAbbrechen, laeuft]);
  const knopfStil = { height: 50, border: 'none', borderRadius: 16, fontFamily: 'inherit', fontSize: 15, fontWeight: 800 } as const;

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 450, background: 'rgba(0,0,0,0.35)', fontFamily: "'Manrope', -apple-system, sans-serif" }}
      onMouseDown={(e) => { if (e.target === e.currentTarget && !laeuft) onAbbrechen(); }}>
      <div ref={dialog} tabIndex={-1} role="alertdialog" aria-modal="true" aria-label={titel} className="immo-glas-stark immo-side-in flex w-full flex-col outline-none"
        style={{ maxWidth: 400, borderRadius: 30, padding: 20, gap: 16, color: 'var(--ink)' }}>
        <div className="flex flex-col items-center text-center" style={{ gap: 10, padding: '8px 4px 0' }}>
          <span className="flex items-center justify-center" style={{ width: 68, height: 68, borderRadius: '50%', background: 'var(--bad-soft)' }}>
            <I size={34} weight="fill" color="var(--bad)" />
          </span>
          <span style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.01em' }}>{titel}</span>
          <span style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--ink2)' }}>{text}</span>
        </div>
        <div className="flex flex-col" style={{ gap: 8 }}>
          <button type="button" onClick={onBestaetigen} disabled={laeuft} data-tip={tip} className="flex items-center justify-center"
            style={{ ...knopfStil, gap: 8, cursor: laeuft ? 'wait' : 'pointer', background: 'var(--bad)', color: '#fff', opacity: laeuft ? 0.7 : 1 }}>
            {laeuft ? <CircleNotch size={16} weight="bold" className="animate-spin" /> : <Trash size={16} weight="bold" />}{knopf}
          </button>
          <button ref={abbrechen} type="button" onClick={onAbbrechen} disabled={laeuft}
            style={{ ...knopfStil, cursor: 'pointer', background: 'var(--fill2)', color: 'var(--ink)' }}>
            Abbrechen
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Lösch-Rückfrage für einen Mitarbeiter (gleicher Text wie bisher). */
export function MitarbeiterLoeschenDialog({ name, laeuft, onBestaetigen, onAbbrechen }: { name: string; laeuft: boolean; onBestaetigen: () => void; onAbbrechen: () => void }) {
  return (
    <GefahrDialog titel="Mitarbeiter löschen?" text={`Der Zugang für ${name} wird unwiderruflich gesperrt.`} knopf="Löschen bestätigen"
      tip="Löschen bestätigen – Zugang und Profil entfernen" laeuft={laeuft} onBestaetigen={onBestaetigen} onAbbrechen={onAbbrechen} />
  );
}
